import moment from 'moment';
import { TransferReservationBaseFiltersFormType } from '../../viajeTraslado/components/ViajeTrasladoBaseFilters';
import { SortState } from '../../viajeTraslado/utils/coordinateStorage';

export interface GestionDeleteColumnVisibility {
  schedule: boolean;
  cliente: boolean;
  puntos: boolean;
  coordinationObservation: boolean;
  serviceType: boolean;
  id: boolean;
  platformId: boolean;
  assigned_driver: boolean;
  state: boolean;
  actions: boolean;
}

export const columnsLabels: Record<
  keyof GestionDeleteColumnVisibility,
  string
> = {
  schedule: 'Horarios',
  cliente: 'Cliente',
  puntos: 'Domicilios',
  coordinationObservation: 'Obs. Coordinación',
  serviceType: 'Servicio',
  id: 'ID',
  platformId: 'Plataforma',
  assigned_driver: 'Chofer Asignado',
  state: 'Estado',
  actions: 'Acciones'
};

export interface GestionDeleteReservesState {
  filters: TransferReservationBaseFiltersFormType;
  page: number;
  rowsPerPage: number;
  sort: SortState;
  columnVisibility: GestionDeleteColumnVisibility;
  lastAccessDate?: string;
}

const STORAGE_KEY = 'deleteReservesState';

export const getDefaultFilters =
  (): TransferReservationBaseFiltersFormType => ({
    dateFrom: moment().startOf('day'),
    dateTo: moment().endOf('day'),
    clientId: null,
    client_type: 'AllClient',
    serviceType: 'Todos',
    hasPriority: false,
    passangerName: '',
    street: '',
    locality: '',
    phone: '',
    client_phone: '',
    reservationId: '',
    state: 'Todos',
    assigned_driver_id: null,
    pre_assigned_driver_id: null,
    driverCode: '',
    oficial_cuenta_id: null,
    authorized_id: null,
    cost_center: '',
    sub_cost_center: '',
    buy_order: '',
    sector: '',
    driver_with_close_shift: false,
    departure_time_exceeded: false,
    sent_reservations_platform: false,
    not_sent_reservations_platform: false,
    has_platform_id: false,
    is_event: false,
    event_name: '',
    is_llt_pais: false,
    bus_type_id: []
  });

export const getDefaultColumnVisibility =
  (): GestionDeleteColumnVisibility => ({
    schedule: true,
    cliente: true,
    puntos: true,
    coordinationObservation: true,
    serviceType: true,
    id: true,
    platformId: true,
    assigned_driver: true,
    state: true,
    actions: true
  });

export const getDefaultSort = (): SortState => ({
  sortDir: 'ASC',
  sortBy: 'created_at'
});

const getDefaultState = (): GestionDeleteReservesState => ({
  filters: getDefaultFilters(),
  page: 1,
  rowsPerPage: 100,
  sort: getDefaultSort(),
  columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
  storedState: Partial<GestionDeleteReservesState>
): GestionDeleteReservesState => {
  const today = moment().format('YYYY-MM-DD');
  const lastAccessDate = storedState.lastAccessDate;
  const isNewDay = !lastAccessDate || lastAccessDate !== today;

  const defaultFilters = getDefaultFilters();
  const storedFilters = storedState.filters ?? defaultFilters;

  return {
    ...getDefaultState(),
    ...storedState,
    filters: {
      ...defaultFilters,
      ...storedFilters,
      dateFrom: isNewDay
        ? moment().startOf('day')
        : storedFilters.dateFrom
        ? moment(storedFilters.dateFrom)
        : null,
      dateTo: isNewDay
        ? moment().endOf('day')
        : storedFilters.dateTo
        ? moment(storedFilters.dateTo)
        : null
    },
    lastAccessDate: today
  };
};

export const loadDeleteStateFromLocalStorage =
  (): GestionDeleteReservesState => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return deserializeState(parsed);
      }
    } catch (error) {
      console.error('Error loading delete reserves state:', error);
    }
    return getDefaultState();
  };

export const saveDeleteStateToLocalStorage = (
  state: GestionDeleteReservesState
): void => {
  try {
    const stateWithDate = {
      ...state,
      filters: {
        ...state.filters,
        dateFrom: state.filters.dateFrom
          ? state.filters.dateFrom.toISOString()
          : null,
        dateTo: state.filters.dateTo ? state.filters.dateTo.toISOString() : null
      },
      lastAccessDate: moment().format('YYYY-MM-DD')
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
  } catch (error) {
    console.error('Error saving delete reserves state:', error);
  }
};

export const clearDeleteStateFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing delete reserves state:', error);
  }
};
